import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { fetchUpdateUser } from '@/services/noteGoApi/noteGoApiComponents';
import type { UserUpdate } from '@/services/noteGoApi/noteGoApiSchemas';
import { errorReporter } from '@/services/errorReportingService';
import { fetchFormatTypes } from '@/features/noteFormats/services/noteFormatService';
import type { FormatType } from '@/features/noteFormats/types';
import type { UserWithZapier } from '../types';

const ZAPIER_WEBHOOK_PREFIX = 'https://hooks.zapier.com/';

export const useZapierFormatWebhooks = () => {
  const { userData, userToken, fetchAndSetUserData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const zapierUser = userData as UserWithZapier | null;

  const [formatTypes, setFormatTypes] = useState<FormatType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [values, setValues] = useState<Record<string, string>>({});
  const [lastSavedValues, setLastSavedValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Load format types on mount
  useEffect(() => {
    let cancelled = false;

    const loadTypes = async () => {
      try {
        const types = await fetchFormatTypes();
        if (!cancelled) setFormatTypes(types);
      } catch (error) {
        if (!cancelled) {
          errorReporter.expected('Failed to load format types for Zapier settings', error, {
            feature: 'zapier',
            operation: 'loadFormatTypes',
          });
        }
      } finally {
        if (!cancelled) setIsLoadingTypes(false);
      }
    };

    loadTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize values from user data
  useEffect(() => {
    const saved = zapierUser?.zapier_format_webhooks ?? {};
    setValues(saved);
    setLastSavedValues(saved);
  }, [zapierUser?.zapier_format_webhooks]);

  const isValidUrl = useCallback((url: string) => {
    const trimmed = url.trim();
    return trimmed === '' || trimmed.startsWith(ZAPIER_WEBHOOK_PREFIX);
  }, []);

  const handleChange = useCallback((promptValue: string, url: string) => {
    setValues((prev) => ({ ...prev, [promptValue]: url }));
  }, []);

  const allValid = useMemo(() => Object.values(values).every(isValidUrl), [values, isValidUrl]);

  const isDirty = useMemo(() => {
    for (const ft of formatTypes) {
      const current = (values[ft.prompt_value] ?? '').trim();
      const saved = (lastSavedValues[ft.prompt_value] ?? '').trim();
      if (current !== saved) return true;
    }
    return false;
  }, [formatTypes, values, lastSavedValues]);

  const handleSave = useCallback(async () => {
    if (!userData?.id || !userToken) {
      showAlert('Unable to save your webhook URLs right now. Please try again later.', 'error');
      return;
    }

    if (!allValid) {
      showAlert('One or more webhook URLs are invalid. Each must start with https://hooks.zapier.com/', 'error');
      return;
    }

    setIsSaving(true);

    // Build the sanitized map, omitting empty values
    const sanitized: Record<string, string> = {};
    for (const ft of formatTypes) {
      const trimmed = (values[ft.prompt_value] ?? '').trim();
      if (trimmed) {
        sanitized[ft.prompt_value] = trimmed;
      }
    }

    try {
      await fetchUpdateUser({
        headers: { authorization: `Bearer ${userToken}` },
        pathParams: { userId: userData.id },
        body: {
          zapier_format_webhooks: Object.keys(sanitized).length > 0 ? sanitized : null,
        } as UserUpdate & Pick<UserWithZapier, 'zapier_format_webhooks'>,
      });

      try {
        await fetchAndSetUserData({ force: true });
        setLastSavedValues(sanitized);
        setValues(sanitized);
        showAlert('Your per-format Zapier webhook URLs have been saved.', 'success');
      } catch (refreshError) {
        errorReporter.expected('Failed to refresh user data after updating format webhooks', refreshError, {
          feature: 'zapier',
          operation: 'refreshAfterFormatSave',
        });
        setLastSavedValues(sanitized);
        setValues(sanitized);
        showAlert(
          'Your webhook URLs were saved, but we could not refresh your data. Please reload the page to see the latest information.',
          'warning',
        );
      }
    } catch (error) {
      errorReporter.expected('Failed to update Zapier format webhook URLs', error, {
        feature: 'zapier',
        operation: 'saveFormatWebhooks',
      });
      showAlert('Failed to save your webhook URLs. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [allValid, fetchAndSetUserData, formatTypes, showAlert, userData?.id, userToken, values]);

  return {
    formatTypes,
    isLoadingTypes,
    values,
    onChange: handleChange,
    onSave: handleSave,
    isSaving,
    isDirty,
    allValid,
    isValidUrl,
  };
};
